import type { ReactNode } from "react";

import { useTranslation } from "react-i18next";

import { SaveIcon } from "lucide-react";

import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

import type { EditChange } from "./changes";

type UnsavedChangesModalProps = {
  open: boolean;
  changes: EditChange[];
  saving: boolean;
  saveLabel?: ReactNode;
  saveDisabled?: boolean;
  onSave?: () => void;
  onDiscard: () => void;
  onContinue: () => void;
};

const Category = styled.h4`
  margin: 1em 0 0.25em;
  font-size: 14px;
`;

const List = styled.ul`
  margin: 0;
  padding-left: 1.25em;

  li + li {
    margin-top: 0.25em;
  }
`;

/** Asks what to do with unsaved changes before leaving the page. */
export function UnsavedChangesModal({
  open,
  changes,
  saving,
  saveLabel,
  saveDisabled,
  onSave,
  onDiscard,
  onContinue,
}: UnsavedChangesModalProps) {
  const { t } = useTranslation();

  const categories = new Map<string, string[]>();
  for (const change of changes) {
    const category = change.category ?? "";
    categories.set(category, [
      ...(categories.get(category) ?? []),
      change.text,
    ]);
  }

  return (
    <Modal open={open} size="small" onClose={onContinue}>
      <ModalHeader>{t("generic.unsavedChanges.title")}</ModalHeader>
      <ModalContent scrolling>
        <p>
          {t(
            changes.length
              ? "generic.editToolbar.changes.intro"
              : "generic.unsavedChanges.leave",
          )}
        </p>
        {[...categories].map(([category, texts]) => (
          <section key={category}>
            {category ? <Category>{category}</Category> : null}
            <List>
              {texts.map((text, index) => (
                <li key={index}>{text}</li>
              ))}
            </List>
          </section>
        ))}
      </ModalContent>
      <ModalActions>
        <Button variant="ghost" disabled={saving} onClick={onContinue}>
          {t("generic.editToolbar.changes.continue")}
        </Button>
        <Button variant="outline" disabled={saving} onClick={onDiscard}>
          {t("generic.discard")}
        </Button>
        {onSave ? (
          <Button
            color="primary"
            disabled={saveDisabled}
            loading={saving}
            onClick={onSave}
          >
            <Icon icon={SaveIcon} />
            {saveLabel ?? t("generic.save")}
          </Button>
        ) : null}
      </ModalActions>
    </Modal>
  );
}
